import React, { useEffect, useRef, useState } from 'react';
import {
    Anchor,
    Box,
    Button,
    Heading,
    Layer,
    Text,
    TextArea,
    ThemeContext,
} from 'grommet';
import PropTypes from 'prop-types';
import { DocumentDownload } from 'grommet-icons';
import { ResumeEditor } from './ResumeEditor';
import { ResumeContentViewer } from './ResumeContentViewer';
import { ResumeComments } from './ResumeComments';
import {
    approveResume,
    rejectResume,
    requestResumeEdits,
    submitResumeChanges,
} from '../pages/ContactProfile/ResumeActions';

// The theme's default button hover is green; reject actions hover red.
const rejectHoverTheme = {
    button: {
        hover: {
            background: { color: 'enrich-red' },
            color: 'white',
            border: { color: 'enrich-red' },
        },
    },
};

// requestResumeEdits swallows its errors and resolves undefined, so treat an
// empty result as a failure here rather than changing the helper.
const requireResult = async (action) => {
    const result = await action();
    if (!result) throw new Error('Action returned no result');
    return result;
};

export const ResumeReviewModal = ({
    contactId,
    documentId,
    content,
    label,
    onComplete,
}) => {
    const [showModal, setShowModal] = useState(false);
    const [editedContent, setEditedContent] = useState(content || '');
    const [activeBlockId, setActiveBlockId] = useState(null);
    const [busy, setBusy] = useState(false);
    const [downloadBusy, setDownloadBusy] = useState(false);
    const [isRejecting, setIsRejecting] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const printableContentRef = useRef(null);
    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const handleDownload = async () => {
        setDownloadBusy(true);
        setErrorMessage('');
        try {
            const printTarget =
                printableContentRef.current?.querySelector('.ProseMirror') ||
                printableContentRef.current;
            if (!printTarget) throw new Error('Nothing to print');
            // Loaded on demand: html2canvas + jspdf are ~1MB, and jspdf ships
            // ESM that every suite importing the barrel would otherwise have to
            // transform.
            const { downloadResumePdf } =
                await import('../helpers/downloadResumePdf');
            await downloadResumePdf(printTarget, 'resume.pdf');
        } catch (error) {
            console.log(error);
            if (isMounted.current) {
                setErrorMessage('Could not build the PDF. Please try again.');
            }
        }
        if (isMounted.current) setDownloadBusy(false);
    };

    const closeModal = () => {
        setShowModal(false);
        setIsRejecting(false);
        setRejectReason('');
        setErrorMessage('');
    };

    const runAction = async (action, failureMessage) => {
        setBusy(true);
        setErrorMessage('');
        try {
            await action();
            onComplete && onComplete(contactId);
            closeModal();
        } catch (error) {
            console.log(error);
            if (isMounted.current) {
                setErrorMessage(
                    error.response?.data?.error?.message || failureMessage,
                );
            }
        }
        if (isMounted.current) setBusy(false);
    };

    const handleReject = () =>
        runAction(
            () => rejectResume(contactId, rejectReason),
            'Could not reject the resume. Please try again.',
        );

    const handleRequestEdits = () =>
        runAction(
            () => requireResult(() => requestResumeEdits(contactId)),
            'Could not request edits. Please try again.',
        );

    const handleSubmitChanges = () =>
        runAction(
            () => submitResumeChanges(contactId, editedContent),
            'Could not submit your changes. Please try again.',
        );

    const handleApprove = () =>
        runAction(
            () => approveResume(contactId),
            'Could not approve the resume. Please try again.',
        );

    const openModal = () => {
        setEditedContent(content || '');
        setErrorMessage('');
        setShowModal(true);
    };

    return (
        <>
            <Anchor label={label} onClick={openModal} />
            {showModal && (
                <Layer
                    onEsc={closeModal}
                    onClickOutside={closeModal}
                    margin="medium">
                    <Box
                        pad="medium"
                        gap="small"
                        width="xlarge"
                        height={{ max: '90vh' }}
                        overflow={{ vertical: 'auto' }}>
                        <Box
                            direction="row"
                            justify="between"
                            align="center"
                            flex={false}>
                            <Heading level="4" margin="none">
                                Review resume
                            </Heading>
                            <Button label="Close" onClick={closeModal} />
                        </Box>
                        <Text size="small" flex={false}>
                            Edit the resume directly, then submit your changes
                            for the student to accept. Or reject it if it
                            doesn&apos;t meet the minimum standard.
                        </Text>
                        <Box flex={false}>
                            <ResumeEditor
                                content={content}
                                onReady={setEditedContent}
                                onChange={setEditedContent}
                                onActiveBlockChange={setActiveBlockId}
                            />
                        </Box>
                        <Box flex={false}>
                            <ResumeComments
                                documentId={documentId}
                                content={editedContent}
                                activeBlockId={activeBlockId}
                            />
                        </Box>
                        <Box style={{ position: 'absolute', left: '-9999px' }}>
                            <Box ref={printableContentRef}>
                                <ResumeContentViewer content={editedContent} />
                            </Box>
                        </Box>
                        {isRejecting && (
                            <Box gap="xsmall" flex={false}>
                                <Text size="small">
                                    <b>Reason for rejection (optional)</b> — the
                                    student will be notified to follow the
                                    directions.
                                </Text>
                                <TextArea
                                    value={rejectReason}
                                    onChange={(event) =>
                                        setRejectReason(event.target.value)
                                    }
                                    placeholder="What needs to be fixed before resubmitting?"
                                />
                            </Box>
                        )}
                        {errorMessage && (
                            <Text
                                size="small"
                                color="status-critical"
                                role="alert"
                                flex={false}>
                                {errorMessage}
                            </Text>
                        )}
                        <Box
                            direction="row"
                            gap="small"
                            justify="end"
                            wrap
                            flex={false}>
                            <Button
                                label="Download PDF"
                                icon={<DocumentDownload />}
                                busy={downloadBusy}
                                onClick={handleDownload}
                            />
                            <ThemeContext.Extend value={rejectHoverTheme}>
                                {!isRejecting && (
                                    <Button
                                        label="Reject"
                                        color="status-critical"
                                        disabled={busy}
                                        onClick={() => setIsRejecting(true)}
                                    />
                                )}
                                {isRejecting && (
                                    <Button
                                        label="Confirm rejection"
                                        color="status-critical"
                                        primary
                                        busy={busy}
                                        onClick={handleReject}
                                    />
                                )}
                            </ThemeContext.Extend>
                            {!isRejecting && (
                                <Button
                                    label="Request Edits"
                                    busy={busy}
                                    onClick={handleRequestEdits}
                                />
                            )}
                            <Button
                                label="Submit Changes"
                                busy={busy}
                                onClick={handleSubmitChanges}
                            />
                            <Button
                                primary
                                label="Approve Resume"
                                busy={busy}
                                onClick={handleApprove}
                            />
                        </Box>
                    </Box>
                </Layer>
            )}
        </>
    );
};

ResumeReviewModal.propTypes = {
    contactId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    content: PropTypes.string,
    label: PropTypes.string,
    onComplete: PropTypes.func,
};

ResumeReviewModal.defaultProps = {
    label: 'Review Resume',
};
